import React from 'react';
import {
  Box,
  Button,
  ButtonText,
  HStack,
  Icon,
  Input,
  InputField,
  Pressable,
  Text,
  Textarea,
  TextareaInput,
  VStack,
} from '@gluestack-ui/themed';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { OralExamQuestion } from '@/common/api/models/oralExamQuestion';
import {
  OralExamQuestionText,
  getOralExamQuestionTitle,
} from '@/common/components/OralExamQuestionText';
import {
  getFillValidationError,
  hasFillSlot,
  MAX_FILL_CHARACTERS,
  MAX_FILL_WORDS,
  MAX_ORAL_EXAM_FILLS,
  type OralExamFillKind,
} from '@/common/components/oralExamPreviewQuestion';

/** Until orval includes ``fills`` / ``fillKind``, intersect locally. */
export type OralExamQuestionWithFills = OralExamQuestion & {
  fills?: string[];
  fillKind?: OralExamFillKind | null;
};

type ProvenanceQuestion = Pick<OralExamQuestion, 'referringTaskIds' | 'referringResourceIds'>;

export type TaskProvenanceMeta = {
  title: string;
  level?: number | null;
};

type ProvenanceTranslate = (
  key: string,
  options?: { title?: string; level?: number },
) => string;

const formatTaskProvenancePart = (
  taskId: string,
  taskMetaById: Readonly<Record<string, TaskProvenanceMeta>>,
  t: ProvenanceTranslate,
): string => {
  const meta = taskMetaById[taskId];
  const title = meta?.title ?? t('lecturer.oralExamConfig.unknownTask');
  const level = meta?.level;
  if (typeof level === 'number' && Number.isFinite(level)) {
    return t('lecturer.oralExamConfig.provenanceFromTask', { level, title });
  }
  return t('lecturer.oralExamConfig.provenanceFromTaskNoLevel', { title });
};

export const formatQuestionProvenanceLabel = (
  question: ProvenanceQuestion,
  taskMetaById: Readonly<Record<string, TaskProvenanceMeta>>,
  resourceTitleById: Readonly<Record<string, string>>,
  t: ProvenanceTranslate,
): string => {
  const taskIds = question.referringTaskIds ?? [];
  if (taskIds.length > 0) {
    return taskIds.map((id) => formatTaskProvenancePart(id, taskMetaById, t)).join(', ');
  }

  const resourceIds = question.referringResourceIds ?? [];
  if (resourceIds.length > 0) {
    return resourceIds
      .map((id) => t('lecturer.oralExamConfig.provenanceFromResource', {
        title: resourceTitleById[id] ?? t('lecturer.oralExamConfig.unknownResource'),
      }))
      .join(', ');
  }

  return t('lecturer.oralExamConfig.provenanceManual');
};

interface EditableQuestionItemProps {
  question: OralExamQuestionWithFills;
  index: number;
  onChange: (updated: OralExamQuestionWithFills) => void;
  onDelete: () => void;
  isReadOnly?: boolean;
  taskMetaById: Readonly<Record<string, TaskProvenanceMeta>>;
  resourceTitleById: Readonly<Record<string, string>>;
}

export const EditableQuestionItem: React.FC<EditableQuestionItemProps> = ({
  question,
  index,
  onChange,
  onDelete,
  isReadOnly = false,
  taskMetaById,
  resourceTitleById,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);

  const provenanceLabel = formatQuestionProvenanceLabel(
    question,
    taskMetaById,
    resourceTitleById,
    t,
  );

  const update = (patch: Partial<OralExamQuestionWithFills>) => onChange({ ...question, ...patch });

  const fills = question.fills ?? [];
  const showFillUi = hasFillSlot(question.question);
  const canAddFill = fills.length < MAX_ORAL_EXAM_FILLS;
  const expandedTitle = getOralExamQuestionTitle(question.question)
    || t('lecturer.oralExamConfig.questionLabel');

  const toggleExpanded = () => setExpanded((v) => !v);

  const updateFill = (fillIdx: number, value: string) => {
    const next = fills.map((entry, i) => (i === fillIdx ? value : entry));
    update({ fills: next });
  };

  const addFill = () => {
    if (!canAddFill) return;
    update({ fills: [...fills, ''] });
  };

  const removeFill = (fillIdx: number) => {
    update({ fills: fills.filter((_, i) => i !== fillIdx) });
  };

  const provenance = (
    <Text
      size="xs"
      color="$textHint"
      numberOfLines={1}
      testID={`oral-exam-question-provenance-${index}`}
    >
      {provenanceLabel}
    </Text>
  );

  return (
    <Box borderWidth={1} borderColor="$borderLight" borderRadius="$md" overflow="hidden">
      <VStack
        px="$3"
        py="$2"
        bg={expanded ? '$bgApp' : '$bgSubtle'}
        space="sm"
      >
        <HStack space="sm" alignItems={expanded ? 'center' : 'flex-start'}>
          <Pressable
            onPress={toggleExpanded}
            hitSlop={8}
            h="$5"
            w="$5"
            mt={expanded ? undefined : '$0.5'}
            justifyContent="center"
            alignItems="center"
            testID={`oral-exam-question-header-${index}`}
          >
            <Icon as={expanded ? ChevronDown : ChevronRight} size="xs" color="$textSubtle" />
          </Pressable>

          {expanded ? (
            <Pressable onPress={toggleExpanded} flex={1}>
              <Text size="sm" fontWeight="bold" numberOfLines={1} color="$textHeading">
                {expandedTitle}
              </Text>
            </Pressable>
          ) : question.question ? (
            <Pressable onPress={toggleExpanded} flex={1}>
              <OralExamQuestionText
                question={question.question}
                size="sm"
                numberOfLines={2}
                showFillSlot
                fillKind={question.fillKind}
                fillKindBadgeTestID={`oral-exam-fill-kind-badge-${index}`}
              />
            </Pressable>
          ) : (
            <Pressable onPress={toggleExpanded} flex={1}>
              <Text size="sm" color="$textBody">
                {t('lecturer.oralExamConfig.questionLabel')}
              </Text>
            </Pressable>
          )}

          {!isReadOnly && (
            <Pressable
              onPress={onDelete}
              hitSlop={8}
              h="$5"
              w="$5"
              mt={expanded ? undefined : '$0.5'}
              justifyContent="center"
              alignItems="center"
              testID={`oral-exam-question-delete-${index}`}
            >
              <Icon as={Trash2} size="xs" color="$statusError" />
            </Pressable>
          )}
        </HStack>

        {expanded && (
          <HStack space="sm" alignItems="flex-start">
            <Box w="$5" />
            <VStack flex={1} space="sm">
              <VStack space="xs">
                <Text size="2xs" color="$textSubtle" fontWeight="medium" textTransform="uppercase">
                  {t('lecturer.oralExamConfig.questionLabel')}
                </Text>
                <Textarea size="sm" h={140}>
                  <TextareaInput
                    value={question.question}
                    onChangeText={(v) => update({ question: v })}
                    placeholder={t('lecturer.oralExamConfig.questionLabel')}
                    multiline
                    testID={`oral-exam-question-text-${index}`}
                  />
                </Textarea>
              </VStack>

              {showFillUi && (
                <VStack space="xs">
                  <Text size="2xs" color="$textSubtle" fontWeight="medium" textTransform="uppercase">
                    {t('lecturer.oralExamConfig.fillsTitle')}
                  </Text>

                  {fills.map((fill, fillIdx) => {
                    const validationError = getFillValidationError(fill);
                    return (
                      <VStack
                        // eslint-disable-next-line react/no-array-index-key
                        key={fillIdx}
                        space="xs"
                      >
                        <HStack space="xs" alignItems="flex-start">
                          <Input flex={1} size="sm">
                            <InputField
                              value={fill}
                              onChangeText={(v) => updateFill(fillIdx, v)}
                              placeholder={t('lecturer.oralExamConfig.fillPlaceholder')}
                              testID={`oral-exam-fill-input-${index}-${fillIdx}`}
                            />
                          </Input>
                          <Pressable
                            onPress={() => removeFill(fillIdx)}
                            hitSlop={8}
                            mt="$2"
                            testID={`oral-exam-fill-remove-${index}-${fillIdx}`}
                          >
                            <Icon as={Trash2} size="xs" color="$statusError" />
                          </Pressable>
                        </HStack>
                        {!!validationError && (
                          <Text
                            size="2xs"
                            color="$statusError"
                            testID={`oral-exam-fill-error-${index}-${fillIdx}`}
                          >
                            {validationError === 'tooManyWords'
                              ? t('lecturer.oralExamConfig.fillErrorTooManyWords', { max: MAX_FILL_WORDS })
                              : t('lecturer.oralExamConfig.fillErrorTooManyCharacters', { max: MAX_FILL_CHARACTERS })}
                          </Text>
                        )}
                      </VStack>
                    );
                  })}

                  {canAddFill && (
                    <Button
                      size="xs"
                      variant="link"
                      action="secondary"
                      onPress={addFill}
                      alignSelf="flex-start"
                      testID={`oral-exam-fill-add-${index}`}
                    >
                      <ButtonText>
                        {'+ '}
                        {t('lecturer.oralExamConfig.fillAdd')}
                      </ButtonText>
                    </Button>
                  )}
                </VStack>
              )}
            </VStack>
            <Box w="$5" />
          </HStack>
        )}

        <HStack space="sm">
          <Box w="$5" />
          <Box flex={1}>{provenance}</Box>
          <Box w="$5" />
        </HStack>
      </VStack>
    </Box>
  );
};

export default EditableQuestionItem;
